import { cached, tracked } from '@glimmer/tracking';

import graphsConfig from 'rise-ui/config/time-series/graphs.js';
import { isArray, toArray } from 'rise-ui/utils/array.js';
import { eq } from 'rise-ui/utils/choice.js';
import { getGraphTextSize } from 'rise-ui/utils/canvas/graph-text.js';
import { getGraphAxisSize } from 'rise-ui/utils/canvas/graph-axis.js';
import { scale } from 'rise-ui/utils/d3.js';
import { call, isFn } from 'rise-ui/utils/function.js';
import { getCanvasTextSize, getContext } from 'rise-ui/utils/canvas.js';
import { getGraphLegendSize } from 'rise-ui/utils/canvas/graph-legend.js';

const floor = v => v || v === 0 ? Math.floor(v) : v;

export const scalar = v => v && (v instanceof Date ? v.getTime() : v);

// Notes on this pattern:
// Difficult to troubleshoot `changeWith` dependencies in reverse to determine
// which dependency activated the change.
// Difficult to troubleshoot `changed` state when property A only used to cause
// property B to `changeWith` A, yet A never becoming unset because it need not
// and is not `consume`d.
export class Changes {
  _changes = {};
  _values = {};
  _affected = {};

  set(key, isChanged) {
    this._changes[key] = isChanged;
    if (isChanged) {
      const affected = this._affected[key];
      if (affected) {
        for (let i = 0; i < affected.length; ++i) {
          this.set(affected[i], isChanged);
        }
      }
    }
    return this;
  }

  change(key, value) {
    const v = this._values[key];
    if (!eq(v, value)) {
      this._values[key] = value;
      this.set(key, true);
    }
    return this;
  }

  changeWith(affectedKey, ...effectKeys) {
    effectKeys.forEach(effectKey =>
      (this._affected[effectKey] = this._affected[effectKey] || [])
        .push(affectedKey)
    );
    return this;
  }

  changed(key) {
    return this._changes[key];
  }

  consume(...keys) {
    let isChanged = false;
    for (let key of keys) {
      isChanged ||= this._changes[key];
      this._changes[key] = false;
    }
    return isChanged;
  }

  value(key) {
    return this._values[key];
  }
}

/**
 * @name GraphConfigInputScaleInput
 * @type {'linear'|'time'}
 */
/**
 * @name GraphConfigInput
 * @property {array} data Data collection for graph
 * @property {string} xKey Datum object key for x-axis
 * @property {string} yKey Datum object key for y-axis
 * @property {string|function} [xLabel] Label to display under the x-axis.
 *   Callback function: `xScale => string`.
 * @property {string|function} [yLabel] Label to display beside the y-axis
 *   Callback function: `yScale => string`.
 * @property {'asc'|'desc'} [xDirection] Direction of x-axis
 * @property {'asc'|'desc'} [yDirection] Direction of y-axis
 * @property {boolean} [altXDirection] True to reverse x-axis direction
 * @property {boolean} [altYDirection] True to reverse y-axis direction
 * @property {boolean} [hideXAxis] (default: false)
 * @property {boolean} [hideYAxis] (default: false)
 * @property {GraphConfigInputScaleInput} [xAxisScale] Scale to use for x-axis
 * @property {GraphConfigInputScaleInput} [yAxisScale] Scale to use for y-axis
 * @property {number} [xTickAngle] Angle of x-axis tick labels
 * @property {number} [yTickAngle] Angle of y-axis tick labels
 * @property {[]|function} [tickArguments] Arguments to pass to d3 scale.ticks
 * @property {function} [tickFormat] `scale => value => string`
 * @property {string} [lineColor] Line color between nodes (default: #000)
 * @property {string} [axisColor] Color of axis lines and ticks
 * @property {string} [fontColor] Font color
 * @property {number} [fontSize] Font size
 * @property {number} [fontMultiplier] Number to multiply all font sizes by
 * @property {number} [padding] Padding width between graph elements (default: 7)
 * @property {number} [graphMargin] Margin width around graph (default: nodeSize / 2)
 * @property {Array.<string|[string, number]>} [headings] Headings displayed above graph, optionally with relative sizes
 * @property {string} [heading] Heading displayed above graph
 * @property {string} [subheading] Secondary heading displayed above graph
 * @property {Array.<string|[string, number]>} [footers] Footers displayed below graph, optionally with relative sizes
 * @property {string} [footer] Footer displayed below graph
 * @property {string} [subfooter] Secondary footer displayed below graph
 * @property {[*, *]} [xDomain] X-axis domain extents
 * @property {[*, *]} [yDomain] Y-axis domain extents
 * @property {number} [nodeSize] Pixel size of drawn nodes (default: 7)
 * @property {'vertical'|'horizontal'|'near'} [cursorType] Type of hovering cursor to use (default: 'near')
 * @property {number} [tooltipDistance] Distance from cursor to look for nodes (default: 13)
 * @property {number} [paddingTop]
 * @property {number} [paddingRight]
 * @property {number} [paddingBottom]
 * @property {number} [paddingLeft]
 * @property {boolean} [legendRight] True to display legend key on right side (default: no legend)
 * @property {boolean} [legendBottom] True to display legend key on bottom side (default: no legend)
 * @property {boolean} [legendGap] Additional gap between legend and graph plot
 */
export class GraphConfigInput {
  @tracked data;
  @tracked dataKeys;
  @tracked dataKeyColors;
  @tracked dataKeyColorsCyclic;
  @tracked dataKeyLabels;
  @tracked dataKeyIndex;
  @tracked xKey;
  @tracked yKey;
  @tracked xLabel;
  @tracked yLabel;
  @tracked xDirection;
  @tracked yDirection;
  @tracked altXDirection;
  @tracked altYDirection;
  @tracked hideXAxis;
  @tracked hideYAxis;
  @tracked xAxisScale;
  @tracked yAxisScale;
  @tracked xTickAngle;
  @tracked yTickAngle;
  @tracked tickArguments;
  @tracked tickFormat;
  @tracked lineColor;
  @tracked axisColor;
  @tracked fontColor;
  @tracked fontSize;
  @tracked fontMultiplier;
  @tracked padding;
  @tracked graphMargin;
  @tracked headings;
  @tracked heading;
  @tracked subheading;
  @tracked subheadingRatio;
  @tracked footers;
  @tracked footer;
  @tracked subfooter;
  @tracked width;
  @tracked height;
  @tracked xDomain;
  @tracked yDomain;
  @tracked nodeSize;
  @tracked cursorType;
  @tracked tooltipDistance;
  @tracked paddingTop;
  @tracked paddingRight;
  @tracked paddingBottom;
  @tracked paddingLeft;
  @tracked legendRight;
  @tracked legendBottom;
  @tracked legendGap;
}

/**
 * Aggregates/calculates graph configuration
 * @name GraphConfig
 * @property {GraphConfigInput} input Consumer should only set properties on
 *   this tracked input object
 * @property {{}} firstDatum First data object (used to guess scale type)
 * @property {string} xLabelValue Label to display under the x-axis
 * @property {string} yLabelValue Label to display beside the y-axis
 * @property {function} xAxisValue Gives x-axis value for given datum
 * @property {function} yAxisValue Gives y-axis value for given datum
 * @property {boolean} xRangeAscending Whether x-axis values are ordered ascending
 * @property {boolean} yRangeAscending Whether y-axis values are ordered ascending
 * @property {string} xScaleType Input xAxisScale or guess based on first datum
 * @property {string} yScaleType Input yAxisScale or guess based on first datum
 * @property {[*, *]} xDomain X-axis domain extents
 * @property {[*, *]} yDomain Y-axis domain extents
 * @property {[number, number]} xRange X-axis range extents
 * @property {[number, number]} yRange Y-axis range extents
 * @property {function} yScale Y-axis domain to range scale function
 * @property {function} xScale X-axis domain to range scale function
 * @property {{}} xAxisOptionsSimple X-axis drawing options for measuring size
 * @property {{}} xAxisOptions X-axis drawing options
 * @property {{}} yAxisOptions Y-axis drawing options
 * @property {number} xAxisTop X-axis top pixel position
 * @property {number} xAxisLeft X-axis left pixel position
 * @property {{width: number, height: number}} xAxisSize X-axis pixel dimensions
 * @property {number} yAxisTop Y-axis top pixel position
 * @property {number} yAxisLeft Y-axis left pixel position
 * @property {{width: number, height: number}} yAxisSize Y-axis pixel dimensions
 * @property {string} lineColor Line color between nodes (default: #000)
 * @property {string} axisColor Color of axis lines and ticks (default: #000)
 * @property {number} padding Padding width between graph elements (default: 7)
 * @property {number} graphMargin Margin width around graph (default: nodeSize / 2)
 * @property {number} headerHeight Header pixel height
 * @property {{}} headerOptions Draw graph text options for header
 * @property {number} footerHeight Footer pixel height
 * @property {{}} footerOptions Draw graph text options for footer
 * @property {number} width Graph full width
 * @property {number} height Graph full height
 * @property {number} top Top graph edge considering top padding
 * @property {number} right Right graph edge considering right padding
 * @property {number} bottom Bottom graph edge considering bottom padding
 * @property {number} left Left graph edge considering left padding
 * @property {number} graphTop Graph lines top pixel position
 * @property {number} graphLeft Graph lines left pixel position
 * @property {number} graphWidth Graph lines pixel width
 * @property {number} graphHeight Graph lines pixel height
 * @property {number} footerTop Footer pixel top position
 * @property {number} nodeSize Pixel size of drawn nodes (default: 7)
 * @property {number} singleNodeSize Pixel size of drawn nodes (nodeSize / 2)
 * @property {number} tooltipDistance Distance from cursor to look for nodes (default: 13)
 * @property {number} nodeSelectionWidth Width in pixels needed to encompass cursor highlighted nodes
 * @property {number} nodeSelectionHeight Height in pixels needed to encompass cursor highlighted nodes
 * @property {number} nodeSelectionXOffset Half width in pixels needed to encompass cursor highlighted nodes
 * @property {number} nodeSelectionYOffset Half height in pixels needed to encompass cursor highlighted nodes
 * @property {number} legendRightWidth Width if any of a legendRight
 * @property {number} legendRightHeight Height if any of a legendRight
 * @property {number} legendRightTop Top position of a legendRight
 * @property {number} legendRightRight Right position of a legendRight
 * @property {number} legendRightBottom Bottom position of a legendRight
 * @property {number} legendRightLeft Left position of a legendRight
 * @property {number} legendBottomWidth Width if any of a legendBottom
 * @property {number} legendBottomHeight Height if any of a legendBottom
 * @property {number} legendBottomTop Top position of a legendBottom
 * @property {number} legendBottomRight Right position of a legendBottom
 * @property {number} legendBottomBottom Bottom position of a legendBottom
 * @property {number} legendBottomLeft Left position of a legendBottom
 */
export class GraphConfig {
  input = new GraphConfigInput();

  @cached get firstDatum() {
    return toArray(this.input.data)[0];
  }

  @cached get xLabelValue() {
    const { xLabel } = this.input;
    return call(xLabel, xLabel, this.xScale);
  }
  @cached get yLabelValue() {
    const { yLabel } = this.input;
    return call(yLabel, yLabel, this.yScale);
  }

  @cached get xAxisValue() {
    const { xKey } = this.input;
    return d => d?.[xKey];
  }
  @cached get yAxisValue() {
    const { yKey } = this.input;
    return d => d?.[yKey];
  }

  @cached get xRangeAscending() {
    return this._rangeAscending(this.input.altXDirection, this.input.xDirection, this.xScaleType, true);
  }
  @cached get yRangeAscending() {
    return this._rangeAscending(this.input.altYDirection, this.input.yDirection, this.yScaleType, false);
  }

  @cached get xScaleType() {
    return this._getScaleType(this.input.xAxisScale, this.xAxisValue);
  }
  @cached get yScaleType() {
    return this._getScaleType(this.input.yAxisScale, this.yAxisValue);
  }

  @cached get xDomain() {
    return this.input.xDomain || [ null, null ];
  }
  @cached get yDomain() {
    return this.input.yDomain || [ null, null ];
  }

  @cached get xRange() {
    const width = this._graphRangeWidth;
    return this.xRangeAscending ? [ 0, width ] : [ width, 0 ];
  }
  @cached get yRange() {
    const height = this._graphRangeHeight;
    return this.yRangeAscending ? [ 0, height ] : [ height, 0 ];
  }

  @cached get xScale() {
    return scale[this.xScaleType]().range(this.xRange).domain(this.xDomain);
  }
  @cached get yScale() {
    return scale[this.yScaleType]().range(this.yRange).domain(this.yDomain);
  }

  @cached get xAxisOptionsSimple() {
    return {
      ...this._axisOptions,
      label: 'Date',
      orient: 'bottom',
      width: this.width,
      fontColor: this.input.fontColor,
      tickAngle: this.input.xTickAngle,
      tickArguments: this.input.tickArguments,
      tickFormat: call(this.input.tickFormat, null, this._xScaleDomain),
    };
  }

  @cached get tickMaxText() {
    const { tickMaxFormat } = this.input;
    return !isFn(tickMaxFormat) ? ''
       // Widest date
       : tickMaxFormat(new Date('2023-12-30T00:00:00'));
  }

  @cached get xAxisForcedPadding() {
    let {
      context, fontFamily, fontSize, fontMultiplier, lineHeight, tickAngle
    } = this.xAxisOptionsSimple;
    fontSize = fontSize * fontMultiplier;
    const fontOptions = {
      font: fontFamily,
      fontSize,
      lineHeight,
      context,
      rotate: tickAngle,
    };
    return getCanvasTextSize(this.tickMaxText, fontOptions).width;
  }

  @cached get xAxisOptions() {
    return {
      ...this.xAxisOptionsSimple,
      label: this.xLabelValue,
      paddingLeft: this.yAxisRight + this.graphMargin,
    };
  }

  get yAxisTopTextPadding() {
    return this.input.hideYAxis ? 0 : this.input.fontSize / 2;
  }

  @cached get yAxisOptions() {
    return {
      ...this._axisOptions,
      orient: 'left',
      paddingTop: this.yAxisTopTextPadding + this.graphMargin,
      height: this.graphHeight + this.yAxisTopTextPadding * 2,
      fontColor: this.input.fontColor,
      label: this.yLabelValue,
      tickAngle: this.input.yTickAngle,
      tickArguments: this.input.tickArguments,
      tickFormat: call(this.input.tickFormat, null, this._yScaleDomain),
    };
  }

  get xAxisTop() {
    return this.xAxisBottom - this._xAxisHeight;
  }
  @cached get xAxisLeft() {
    return 0;
  }
  @cached get xAxisBottom() {
    return this.footerTop;
  }
  @cached get xAxisSize() {
    return this.input.hideXAxis ? { width: 0, height: 0 }
      : getGraphAxisSize(this.xScale, this.xAxisOptions);
  }

  get yAxisTop() {
    return Math.max(0, Math.ceil(this.headerBottom - this.yAxisTopTextPadding));
  }
  get yAxisLeft() {
    const { xAxisForcedPadding: p, _yAxisWidth: w } = this;
    return this.left + (p ? Math.max(0, p - w) : 0);
  }
  @cached get yAxisRight() {
    return this.yAxisLeft + this._yAxisWidth;
  }
  @cached get _yAxisWidth() {
    return this.input.hideYAxis ? 0
      : getGraphAxisSize(this._yScaleDomain, this.yAxisOptions).width;
  }
  @cached get yAxisSize() {
    return this.input.hideYAxis ? { width: 0, height: 0 }
      : getGraphAxisSize(this.yScale, this.yAxisOptions);
  }

  @cached get lineColor() {
    return this.input.lineColor || '#000';
  }
  @cached get axisColor() {
    return this.input.axisColor || '#000';
  }

  @cached get padding() {
    const { padding } = this.input;
    return padding || padding === 0 ? padding : 7;
  }

  @cached get graphMargin() {
    const { graphMargin } = this.input;
    return graphMargin || graphMargin === 0 ? graphMargin : Math.ceil(this.nodeSize / 2) + 1;
  }

  @cached get headerHeight() {
    return this.width && getGraphTextSize(this._simpleHeaderOptions).height || 0;
  }
  @cached get headerOptions() {
    return {
      ...this._simpleHeaderOptions,
      height: this.headerHeight || 0,
    };
  }

  @cached get footerHeight() {
    return getGraphTextSize(this._simpleFooterOptions).height;
  }
  @cached get footerOptions() {
    return {
      ...this._simpleFooterOptions,
      height: this.footerHeight || 0,
    };
  }

  @cached get width() {
    return floor(this.input.width || 0);
  }
  @cached get height() {
    return floor(this.input.height || 0);
  }
  @cached get left() {
    return Math.floor(this.input.paddingLeft || 0);
  }
  @cached get top() {
    return Math.floor(this.input.paddingTop || 0);
  }
  @cached get right() {
    return Math.floor(this.width - (this.input.paddingRight || 0));
  }
  @cached get bottom() {
    return Math.floor(this.height - (this.input.paddingBottom || 0));
  }

  get graphTop() {
    return Math.ceil(Math.max(this.yAxisTopTextPadding, this.headerBottom));
  }
  get graphLeft() {
    return Math.ceil(this.yAxisRight);
  }
  @cached get graphRight() {
    return Math.floor(this.legendRightLeft - (this.input.legendGap || 0));
  }
  @cached get graphWidth() {
    return this.graphRight - this.graphLeft;
  }
  @cached get graphHeight() {
    return Math.floor(this._graphBottom - this.graphTop);
  }
  @cached get graphRect() {
    return {
      top: this.graphTop,
      right: this.graphRight,
      bottom: this.graphTop + this.graphHeight,
      left: this.graphLeft,
      width: this.graphWidth,
      height: this.graphHeight,
    };
  }

  @cached get footerTop() {
    return this._footerBottom - this.footerHeight;
  }

  @cached get nodeSize() {
    return this.input.nodeSize || 7;
  }

  @cached get singleNodeSize() {
    return this.nodeSize / 2;
  }

  @cached get tooltipDistance() {
    return this.input.tooltipDistance || 24;
  }

  @cached get nodeSelectionWidth() {
    return Math.ceil(this.input.cursorType === 'horizontal'
      ? this.graphWidth
      : this.tooltipDistance * 2 + this.nodeSize + 1
    );
  }
  @cached get nodeSelectionHeight() {
    return Math.ceil(this.input.cursorType === 'vertical'
      ? this.graphHeight
      : this.tooltipDistance * 2 + this.nodeSize + 1
    );
  }

  @cached get nodeSelectionXOffset() {
    return Math.floor(this.nodeSelectionWidth / 2);
  }
  @cached get nodeSelectionYOffset() {
    return Math.floor(this.nodeSelectionHeight / 2);
  }

  @cached get legendKeys() {
    const {
      dataKeys, dataKeyColors, dataKeyColorsCyclic, dataKeyIndex, dataKeyLabels,
    } = this.input;
    const max = (dataKeys?.length - (dataKeyColorsCyclic ? 0 : 1)) || 1;
    return dataKeys?.map(dk => {
      const index = dataKeyIndex(dk);
      return {
        index,
        color: dataKeyColors(index / max),
        label: dataKeyLabels(dk, index),
      };
    }).sort((a, b) => a.index - b.index) || [];
  }

  @cached get legendMeasureOptions() {
    return {
      context: this.textMetricsContext,
      padding: this.padding,
      fontSize: this.input.fontSize,
      fontMultiplier: this._fontMultiplier * .9,
      minKeySizeRatio: .5,
      minCharLineSize: 10,
      ...(this.legendRight
        ? { height: this.legendRightHeight, maxWidth: this.width * .4 }
        : { width: this.legendBottomWidth, maxHeight: this.height * .4 }
      )
    };
  }

  @cached get legendSize() {
    return (this.legendRight || this.legendBottom) && getGraphLegendSize(
      this.textMetricsContext,
      this.legendKeys,
      this.legendMeasureOptions
    ) || { width: 0, height: 0, count: 0 };
  }

  @cached get legendOptions() {
    return {
      ...this.legendMeasureOptions,
      ...this.legendSize,
      center: true,
      // outline: true,
    };
  }

  @cached get legendRight() {
    return !!this.input.legendRight && this.input.dataKeys?.length > 1;
  }
  @cached get legendBottom() {
    return !!this.input.legendBottom && !this.legendRight
      && this.input.dataKeys?.length > 1;
  }

  @cached get legendRightWidth() {
    return !this.legendRight ? 0 : this.legendSize.width;
  }
  @cached get legendRightHeight() {
    return !this.legendRight ? 0 : this.legendRightBottom - this.legendRightTop;
  }
  get legendRightTop() {
    return this.top;
  }
  get legendRightRight() {
    return this.right;
  }
  get legendRightBottom() {
    return this.bottom;
  }
  @cached get legendRightLeft() {
    return this.legendRightRight - this.legendRightWidth;
  }

  @cached get legendBottomWidth() {
    return !this.legendBottom ? 0
      : this.legendBottomRight - this.legendBottomLeft;
  }
  @cached get legendBottomHeight() {
    return !this.legendBottom ? 0 : this.legendSize.height;
  }
  @cached get legendBottomTop() {
    return this.legendBottomBottom - this.legendBottomHeight;
  }
  @cached get legendBottomRight() {
    return this.right;
  }
  @cached get legendBottomBottom() {
    return this.bottom;
  }
  @cached get legendBottomLeft() {
    return this.left;
  }

  @cached get _headings() {
    return this._normalizeTexts(this.input.headings);
  }
  get _heading() {
    return this.input.heading;
  }
  get _subheading() {
    return this.input.subheading;
  }

  @cached get _footers() {
    return this._normalizeTexts(this.input.footers);
  }
  get _footer() {
    return this.input.footer;
  }
  get _subfooter() {
    return this.input.subfooter;
  }

  _headingSize = graphsConfig.textSize;
  @cached get _subheadingRatio() {
    return this.input.subheadingRatio || graphsConfig.subtextRatio;
  }
  @cached get _subheadingSize() {
    return this._headingSize * this._subheadingRatio;
  }
  get headerTop() {
    return this.top;
  }
  @cached get headerBottom() {
    return this.headerTop + this.headerHeight;
  }
  @cached get headerRight() {
    return this.graphRight;
  }
  @cached get headerLeft() {
    return this.left;
  }
  @cached get headerWidth() {
    return this.headerRight - this.headerLeft;
  }

  textMetricsContext = getContext(document.createElement('canvas'), '2d', null, 1);

  @cached get _simpleHeaderOptions() {
    return {
      width: this.headerWidth,
      fontMultiplier: this._fontMultiplier,
      padding: this.padding,
      texts: this._headings,
      text: this._heading,
      subtext: this._subheading,
      fontColor: this.input.fontColor,
      gap: this.padding,
      subtextRatio: this._subheadingRatio,
      fitText: true,
      balanceText: true,
      context: this.textMetricsContext,
    };
  }
  @cached get _simpleFooterOptions() {
    return {
      width: this.footerWidth,
      fontMultiplier: this._fontMultiplier,
      padding: this.padding,
      texts: this._footers,
      text: this._footer,
      subtext: this._subfooter,
      fontColor: this.input.fontColor,
      gap: this.padding,
      subtextRatio: graphsConfig.subtextRatio,
      context: this.textMetricsContext,
    };
  }

  @cached get _xScaleDomain() {
    return scale[this.xScaleType]().range([ 0, 0 ]).domain(this.xDomain);
  }
  @cached get _xAxisHeight() {
    return this.input.hideXAxis ? 0
      : getGraphAxisSize(this._xScaleDomain, this.xAxisOptionsSimple).height;
  }

  @cached get _yScaleDomain() {
    return scale[this.yScaleType]().range([ 0, 0 ]).domain(this.yDomain);
  }

  @cached get _graphBottom() {
    return this.xAxisTop;
  }
  @cached get _graphRangeWidth() {
    return this.graphWidth - this.graphMargin * 2;
  }
  @cached get _graphRangeHeight() {
    return this.graphHeight - this.graphMargin * 2;
  }

  @cached get _footerTop() {
    return this._footerBottom - (this.footerHeight || 0);
  }
  @cached get _footerBottom() {
    return Math.floor(this.legendBottomTop);
  }
  @cached get footerRight() {
    return this.graphRight;
  }
  @cached get footerLeft() {
    return this.left;
  }
  @cached get footerWidth() {
    return this.footerRight - this.footerLeft;
  }

  @cached get _fontMultiplier() {
    return this.input.fontMultiplier || 1;
  }

  @cached get _axisOptions() {
    return {
      strokeStyle: this.axisColor,
      fontSize: this.input.fontSize,
      fontMultiplier: this._fontMultiplier,
      context: this.textMetricsContext,
    };
  }

  _normalizeTexts(texts) {
    return texts?.map(text => {
      if (!text?.[0]) {
        text = [ '', 0 ];
      } else if (typeof text === 'string') {
        text = [ text, 1 ];
      } else if (isArray(text)) {
        text = [ text[0], text[1] || text[1] === 0 ? text[1] : 1 ];
      }
      return text;
    });
  }

  _getScaleType(givenScale, valueFn) {
    let result = scale[givenScale] && givenScale;
    if (!result) {
      const datum = this.firstDatum;
      if (datum && valueFn(datum) instanceof Date) {
        result = 'time';
      }
    }
    return result || 'linear';
  }

  _rangeAscending(altDirection, direction, scaleType, defaultAsc) {
    let asc = defaultAsc;
    const dir = ('' + direction).toLowerCase();
    if (dir.substr(0, 3) === 'asc') {
      asc = true;
    } else if (dir.substr(0, 4) === 'desc') {
      asc = false;
    } else if (scaleType === 'time') {
      asc = true;
    }
    return altDirection ? !asc : asc;
  }
}
